// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import { UtilityContext } from "../../context/util-context";
import { useSearchParams } from "react-router-dom";
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import ImageSelector from "../UI/ImageSelector";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import { isoDateToString } from "../../util/format-date";
import { MdClose, MdRefresh } from "react-icons/md";
import ImageCarousel from "../UI/ImageCarousel";
import Notes from "../UI/Notes";
import Modal from "../UI/Modal";
import LoadingSpinner from "../UI/LoadingSpinner";
import uploadFile from "../../util/upload-file";
import PrintLabelModal from "./PrintLabelModal";
import LoadingCard from "../UI/LoadingCard";
import PersonCard from "../UI/PersonCard";
import AuditLogs from "../UI/AuditLogs";

// Internal Imports

/** ========================================================================
 * * RepossessionInfo Component
 * This component renders the information of a specific repossession.
 * ========================================================================== */

export default function RepossessionInfo() {
  // Load Context
  const { navigate, setConfirm } = useContext(UtilityContext);

  // Get Booking ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [repo, setRepo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [labelModal, setLabelModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  /* ====================== FETCH REPOSSESSION  ====================== */
  const fetchRepossession = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "repossession",
        id: id,
        columns: [
          "booking { vehicle {make { name image } model year vin cornerImage} customer { firstName lastName id email phone street city state zip photo } startDate endDate id}",
          "initiatedBy { name id }",
          "approvedBy { name id }",
          "postedBy { name id }",
          "recoveredBy { name id }",
        ],
      });

      setRepo(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== UPDATE STATUS ====================== */
  const updateStatus = async (status) => {
    try {
      setLoading(true);
      if (status === "approved") {
        let message =
          "Approving this repossession will notify the recovery team to proceed regardless of the current status.";
        setConfirm({
          message: message,
          action: async () => {
            await requestAdminAPI("edit-repossession", {
              id: id,
              status: status,
            });
            fetchRepossession();
          },
        });
      } else setShowModal(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // USE EFFECT
  useEffect(() => {
    if (id) fetchRepossession();
  }, [id]);

  return (
    <div className="container">
      <UpdateStatusModal
        repo={repo}
        setOpen={setShowModal}
        open={showModal}
        refresh={fetchRepossession}
      />
      <PrintLabelModal
        open={labelModal}
        setOpen={setLabelModal}
        recipient={repo?.booking?.customer}
        refresh={fetchRepossession}
        repossessionId={id}
      />
      <CancelModal
        open={showCancelModal}
        setOpen={setShowCancelModal}
        repo={repo}
        refresh={fetchRepossession}
      />
      <Heading title="Repossession Info" id={id}>
        <AuditLogs table="repossession" id={id} />
        <MdRefresh className="icon" onClick={fetchRepossession} />
      </Heading>
      <div className="grid gap-4 lg:grid-cols-3">
        <div
          className="grid gap-4"
          onClick={() =>
            navigate(`/vehicles/info?id=${repo?.booking?.vehicle?.vin}`)
          }
        >
          <div className="flex items-center space-x-4">
            <img
              src={repo?.booking?.vehicle?.make?.image}
              className="object-contain w-20"
            />
            <div>
              <p className="text-2xl font-bold">
                {repo?.booking?.vehicle?.year}{" "}
                {repo?.booking?.vehicle?.make?.name}{" "}
                {repo?.booking?.vehicle?.model}
              </p>
              <p className="text-base text-stone-400">
                {repo?.booking?.vehicle?.vin}
              </p>
            </div>
          </div>
          <ImageSelector
            loading={loading}
            image={repo?.booking?.vehicle?.cornerImage}
            classes="h-48 lg:w-3/4"
            name="cornerImage"
            label="CORNER IMAGE"
          />
        </div>
        <div className="content-start info lg:grid-cols-3 lg:col-span-2">
          <DetailPair
            label="Date Initiated"
            loading={loading}
            value={`${isoDateToString(repo?.dateCreated)}`}
          />
          <DetailPair
            label="Initiated By"
            loading={loading}
            link={`/employees/info?id=${repo?.initiatedBy?.id}`}
            value={repo?.initiatedBy?.name}
          />
          <DetailPair
            label="Booking Dates"
            loading={loading}
            link={`/bookings/info?id=${repo?.booking?.id}`}
            value={`${isoDateToString(
              repo?.booking?.startDate
            )} - ${isoDateToString(repo?.booking?.endDate)}`}
          />
          <DetailPair
            label="Date Posted"
            loading={loading}
            value={`${isoDateToString(repo?.datePosted)}`}
          />
          <DetailPair
            label="Posted By"
            loading={loading}
            link={`/employees/info?id=${repo?.postedBy?.id}`}
            value={repo?.postedBy?.name}
          />
          <LinkPair
            label="Demand Letter"
            loading={loading}
            link={repo?.demandLetter}
          />
          <DetailPair
            label="Date Approved"
            loading={loading}
            value={`${isoDateToString(repo?.dateApproved)}`}
          />
          <DetailPair
            label="Approved By"
            loading={loading}
            link={`/employees/info?id=${repo?.approvedBy?.id}`}
            value={repo?.approvedBy?.name}
          />
          <LinkPair
            label="Demand Letter Receipt"
            loading={loading}
            link={repo?.demandLetterReceipt}
          />
          <DetailPair
            label="Date Recovered"
            loading={loading}
            value={`${isoDateToString(repo?.dateRecovered)}`}
          />
          <DetailPair
            label="Recovered By"
            loading={loading}
            link={`/employees/info?id=${repo?.recoveredBy?.id}`}
            value={repo?.recoveredBy?.name}
          />
          <DetailPair
            label="Status"
            classes={
              repo?.status === "recovered"
                ? "text-green-600 font-bold text-lg"
                : repo?.status === "posted"
                ? "text-blue-600 font-bold text-lg"
                : repo?.status === "approved"
                ? "text-red-600 font-bold text-lg"
                : repo?.status === "cancelled"
                ? "text-stone-400 font-bold text-lg"
                : "text-yellow-600 font-bold text-lg"
            }
            value={repo?.status?.toUpperCase()}
            loading={loading}
          />
        </div>
        <div className="grid content-start gap-4 text-sm">
          <PersonCard
            link={`/customers/info?id=${repo?.booking?.customer?.id}`}
            name={`${repo?.booking?.customer?.firstName} ${repo?.booking?.customer?.lastName}`}
            image={repo?.booking?.customer?.photo}
            secondary={repo?.booking?.customer?.phone}
            label="Customer"
            loading={loading}
          />
          <DetailPair
            label="Repossession Reason"
            loading={loading}
            value={repo?.reason}
          />
          <DetailPair
            label="Demand Letter Address"
            loading={loading}
            value={repo?.demandLetterAddress}
          />
          {repo?.dateRecovered && (
            <DetailPair
              label="Recovery Location"
              loading={loading}
              value={repo?.recoveryLocation}
            />
          )}
          {repo?.dateCancelled && (
            <DetailPair
              label="Cancellation Reason"
              loading={loading}
              value={repo?.cancellationReason}
            />
          )}
        </div>
        <div className="grid gap-2 text-sm text-stone-400">
          {loading ? (
            <LoadingCard columns={1} max={2} />
          ) : (
            <Fragment>
              <p>Images at Recovery</p>
              <ImageCarousel images={repo?.images} preview={true} />
            </Fragment>
          )}
        </div>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="mt-4 info">
          {!repo?.dateCancelled && !repo?.dateRecovered && (
            <Fragment>
              {!repo?.dateRecovered && (
                <button onClick={updateStatus} className="btn-primary">
                  Update Status
                </button>
              )}
              {repo?.status === "initiated" && (
                <button
                  onClick={() => setLabelModal(true)}
                  className="btn-secondary"
                >
                  Print Label
                </button>
              )}
              {!repo?.dateApproved && (
                <button
                  onClick={() => updateStatus("approved")}
                  className="btn-success"
                >
                  Approve
                </button>
              )}

              <button
                onClick={() => setShowCancelModal(true)}
                className="btn-warning"
              >
                Cancel
              </button>
            </Fragment>
          )}
        </div>
      )}
      <Notes id={repo?.id} table="repossession" show={true} />
    </div>
  );
}

// ========================================================================
// * Cancellation Modal
// ==========================================================================
const CancelModal = ({ open, setOpen, refresh, repo }) => {
  const [loading, setLoading] = useState(false);

  /* ====================== CANCEL REPOSSESSION ====================== */
  const cancelHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        cancellationReason: document.getElementById("cancellationReason").value,
        id: repo.id,
        status: "cancelled",
      };
      await requestAdminAPI("edit-repossession", payload);
      refresh && refresh();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal">
        <Heading
          title="Cancel Repossession"
          text="Please provide a reason for cancelling this repossession."
          isBackArrow={false}
        >
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        <div className="text-sm">
          <DetailPair
            label="Cancellation Reason"
            edit={true}
            inputOptions={{
              id: "cancellationReason",
              type: "text",
              placeholder: "Enter cancellation reason",
            }}
          />
        </div>
        <div className="info">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <button onClick={cancelHandler} className="btn-warning">
              Confrim
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ========================================================================
// * Update Status Modal
// ==========================================================================
const UpdateStatusModal = ({ open, setOpen, refresh, repo }) => {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState("");
  const [uploadProgress, setUploadProgress] = useState(false);

  /* ====================== UPDATE STATUS ====================== */
  const updateStatus = async () => {
    try {
      setLoading(true);
      const payload = {
        id: repo.id,
      };
      if (repo.status === "initiated") {
        payload.status = "posted";
        payload.demandLetterAddress = document.getElementById(
          "demandLetterAddress"
        ).value;
        const demandLetterReceipt = document.getElementById(
          "demandLetterReceipt"
        ).files[0];
        if (demandLetterReceipt) {
          setItem(demandLetterReceipt.name);
          const path = `bookings/${
            repo?.booking.id
          }/demandLetterReceipt.${demandLetterReceipt.name.split(".").pop()}`;
          payload.demandLetterReceipt = await uploadFile(
            path,
            demandLetterReceipt,
            setUploadProgress
          );
        }
      } else if (repo.status === "approved") {
        payload.status = "recovered";
        payload.recoveryLocation =
          document.getElementById("recoveryLocation").value;

        // Upload Recovery Images
        const images = document.getElementById("images").files;
        console.log(images);
        let imageUrls = [];
        if (images.length > 0) {
          const path = `bookings/${repo?.booking.id}/recoveryImages`;
          let index = 0;
          for (const image of images) {
            setItem(image.name);
            index += 1;
            // Get File Extension
            const extension = image.name.split(".").pop();
            const type = image.type.split("/")[0];
            const url = await await uploadFile(
              `${path}_${new Date().toISOString()}_${index}-${type}.${extension}`,
              image,
              setUploadProgress
            );
            imageUrls.push(url);
          }
        }
        payload.images = imageUrls;
      }
      await requestAdminAPI("edit-repossession", payload);
      setOpen(false);
      setUploadProgress(false);
      refresh && refresh();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal">
        <Heading
          title="Update Repossession Status"
          text={
            repo?.status === "initiated"
              ? "Enter the address where you sent the demand letter and upload a receipt of the certified mail."
              : "Enter the recovery location and upload images taken during the recovery."
          }
          isBackArrow={false}
        >
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        {repo?.status === "initiated" && (
          <div className="info lg:grid-cols-3">
            <div className="lg:col-span-2">
              <DetailPair
                label="Demand Letter Address"
                edit={true}
                inputOptions={{
                  id: "demandLetterAddress",
                  type: "text",
                  defaultValue: repo.demandLetterAddress,
                }}
              />
            </div>
            <LinkPair
              label="Demand Letter Receipt"
              edit={true}
              inputOptions={{
                type: "file",
                id: "demandLetterReceipt",
              }}
            />
          </div>
        )}
        {repo?.dateApproved && (
          <div className="info lg:grid-cols-3">
            <div className="lg:col-span-2">
              <DetailPair
                label="Recovery Location"
                edit={true}
                inputOptions={{
                  id: "recoveryLocation",
                  type: "text",
                }}
              />
            </div>
            <LinkPair
              label="Recovery Images"
              edit={true}
              inputOptions={{
                type: "file",
                id: "images",
                multiple: true,
              }}
            />
          </div>
        )}
        {repo?.status === "posted" ? (
          <div className="">
            <p className="warning">
              Repossession must be approved before begining recovery or updating
              status to recovered.
            </p>
          </div>
        ) : (
          <div className="info">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <button onClick={updateStatus} className="btn-primary">
                Confirm
              </button>
            )}
            {uploadProgress && (
              <p>
                Uploading {item}... {uploadProgress}%
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
